import { inject, Injectable, InjectionToken } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export type JsonFetch = (
  input: string | URL | globalThis.Request,
  init?: RequestInit,
) => Promise<Response>;

export const JSON_FETCH = new InjectionToken<JsonFetch>('JSON_FETCH', {
  providedIn: 'root',
  factory: () => globalThis.fetch.bind(globalThis),
});

export const AUTHENTICATED_JSON_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class AuthenticatedJsonClientService {
  private readonly auth = inject(AuthService);
  private readonly fetch = inject(JSON_FETCH);

  async post<ResponseBody>(path: `/${string}`, body: unknown): Promise<ResponseBody> {
    const token = await firstValueFrom(this.auth.idToken());
    if (!token) throw new Error('Authentication is required.');
    const controller = new AbortController();
    let timedOut = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, reject) => {
      timeoutId = globalThis.setTimeout(() => {
        timedOut = true;
        controller.abort();
        reject(new Error('request timeout'));
      }, AUTHENTICATED_JSON_TIMEOUT_MS);
    });
    let response: Response;
    try {
      try {
        response = await Promise.race([
          this.fetch(`${environment.apiUrl}${path}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal: controller.signal,
          }),
          deadline,
        ]);
      } catch {
        throw new Error(
          timedOut
            ? 'The AI assistant request timed out. Try again.'
            : 'The AI assistant could not be reached. Try again.',
        );
      }
      if (!response.ok) throw new Error(this.httpError(response.status));
      try {
        return (await Promise.race([response.json(), deadline])) as ResponseBody;
      } catch {
        throw new Error(
          timedOut
            ? 'The AI assistant request timed out. Try again.'
            : 'The AI assistant returned an invalid response. Try again.',
        );
      }
    } finally {
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
    }
  }

  private httpError(status: number): string {
    if (status === 401 || status === 403)
      return 'You are not authorized to ask questions about this decision.';
    if (status === 409)
      return 'This decision explanation changed. Reload the card before asking again.';
    if (status === 429) return 'The AI assistant is busy. Wait a moment and try again.';
    return 'The AI assistant could not accept this question. Try again.';
  }
}
